import React from "react";
import { cn } from "../cn";

export type EmptyStateProps = {
  /** A lucide icon element. Decorative — it is hidden from assistive tech. */
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** A single action. More than one turns an empty state into a decision. */
  action?: React.ReactNode;
  className?: string;
};

/**
 * What a list shows before it has anything in it.
 *
 * Replaces the `<p className="text-sm text-gray-400 italic">` pattern the app
 * used, which failed contrast at 2.58:1, and — italic, centred, grey — read as
 * an error rather than as a starting point.
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  className,
}) => (
  <div
    className={cn(
      "flex flex-col items-center justify-center gap-3 px-6 py-12 text-center",
      className
    )}
  >
    {icon && (
      <span
        aria-hidden="true"
        className="flex size-12 items-center justify-center rounded-lg bg-surface-sunken text-content-tertiary"
      >
        {icon}
      </span>
    )}
    <p className="text-base font-semibold text-content-primary">{title}</p>
    {description && (
      <p className="max-w-sm text-sm leading-relaxed text-content-secondary">
        {description}
      </p>
    )}
    {action && <div className="mt-2">{action}</div>}
  </div>
);

export default EmptyState;
